import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar, Camera, Check, Clock, CreditCard, DollarSign, Download, Eye, EyeOff, Film, Loader2, MapPin, Paintbrush, Pencil, Plus,
  Power, RefreshCw, RotateCcw, Save, ShieldAlert, Star, Tag, Trash2, Undo2, Upload, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const escapeCsv = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const Pill = ({ color, label, testid }) => (
  <span
    data-testid={testid}
    className={`inline-block px-2 py-1 rounded-sm text-[10px] font-bold tracking-widest border ${color}`}
  >
    {label}
  </span>
);

const EventField = ({ label, helper, value, onChange, testid, icon: Icon }) => (
  <div>
    <div className="flex items-center gap-2 mb-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#E85A2C]" />}
      <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70">{label}</label>
    </div>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C] transition-colors"
    />
    {helper && <div className="mt-1 text-[10px] text-white/40 italic">{helper}</div>}
  </div>
);

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1621851709622-e19c9a4f0cc5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const DETAIL_FALLBACK =
  "https://images.unsplash.com/photo-1534797258760-1bd2cc95a5bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ImagePicker = ({ label, helper, value, fallback, onChange, testid }) => (
  <div className="p-5 rounded-sm border border-white/10 bg-[#0F0B08] space-y-3">
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70">{label}</div>
      {helper && <div className="text-[10px] text-white/40 italic mt-1">{helper}</div>}
    </div>
    <div className="aspect-video rounded-sm overflow-hidden border border-white/10 bg-black/40">
      <img src={value || fallback} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <label
        data-testid={`${testid}-upload`}
        className="cursor-pointer text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-[#E85A2C]/40 text-[#E85A2C] hover:bg-[#E85A2C]/10 transition-colors flex items-center gap-1.5"
      >
        <Upload className="w-3.5 h-3.5" /> Upload photo
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            if (!f.type.startsWith("image/")) return toast.error("Image files only");
            if (f.size > 10_000_000) return toast.error("Keep image under 10MB");
            const reader = new FileReader();
            reader.onload = () => onChange(reader.result);
            reader.readAsDataURL(f);
          }}
        />
      </label>
      {value && (
        <button
          onClick={() => onChange("")}
          data-testid={`${testid}-clear`}
          className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-white/15 text-white/50 hover:text-white transition-colors"
        >
          Use default
        </button>
      )}
    </div>
  </div>
);

const statusPill = (r) => {
  if (r.approval_status === "approved") {
    return <Pill color="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" label="APPROVED" />;
  }
  return <Pill color="bg-[#E85A2C]/15 text-[#E85A2C] border-[#E85A2C]/30" label="PENDING" />;
};

const paidPill = (r) => {
  if (r.paid) {
    return <Pill color="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" label="PAID" />;
  }
  return <Pill color="bg-yellow-500/10 text-yellow-400 border-yellow-500/30" label="UNPAID" />;
};

export default function BbqAdminDashboard({ tokenProp }) {
  const [params] = useSearchParams();
  const token = tokenProp || params.get("token") || "";

  const [tab, setTab] = useState("active"); // active | trash | event | reviews
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pricing, setPricing] = useState({ prices: {}, group_discounts: [], promo_codes: [] });
  const [pricingDraft, setPricingDraft] = useState(pricing);
  const [savingPricing, setSavingPricing] = useState(false);
  const [pageActive, setPageActive] = useState(true);
  const [togglingPage, setTogglingPage] = useState(false);
  const [media, setMedia] = useState({ hero_image: "", detail_image: "", video_id: "", video_updated_at: "", hero_bg_mode: "image" });
  const [mediaDraft, setMediaDraft] = useState({ hero_image: "", detail_image: "" });
  const [galleryRound, setGalleryRound] = useState("");
  const [savingMedia, setSavingMedia] = useState(false);
  const [videoProgress, setVideoProgress] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [paymentEmergency, setPaymentEmergency] = useState(false);
  const [togglingPayMode, setTogglingPayMode] = useState(false);
  const [revenue, setRevenue] = useState({ total: 0, currency: "AUD", count: 0, items: [] });
  const [reviewEdit, setReviewEdit] = useState(null); // { id, name, rating, text }
  const [reviewBusyId, setReviewBusyId] = useState(null);
  const [capacity, setCapacity] = useState(50);
  const [capacityDraft, setCapacityDraft] = useState("50");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, id, name }
  const [event, setEvent] = useState({
    title: "", eyebrow: "",
    date_label: "", date_note: "",
    time_label: "", time_note: "",
    location_label: "", location_note: "",
    location_area: "", location_region: "",
    short_date: "", short_time: "", short_location: "",
  });
  const [eventDraft, setEventDraft] = useState(event);
  const [savingEvent, setSavingEvent] = useState(false);

  const load = async (nextTab = tab) => {
    setLoading(true);
    setError("");
    try {
      if (nextTab === "event") {
        const s = await axios.get(`${API}/bbq-settings`, { params: { token } });
        const evt = s.data.event || {};
        setEvent(evt);
        setEventDraft(evt);
        const cap = Number(s.data.capacity ?? 50);
        setCapacity(cap);
        setCapacityDraft(String(cap));
      } else if (nextTab === "reviews") {
        const s = await axios.get(`${API}/bbq-reviews/admin`, { params: { token } });
        setReviews(s.data.items || []);
      } else if (nextTab === "pricing") {
        // Admin has no separate pricing GET; use public endpoint (returns full active view)
        const s = await axios.get(`${API}/bbq-pricing`);
        const data = {
          prices: s.data.prices || {},
          group_discounts: s.data.group_discounts || [],
          promo_codes: s.data.promo_codes || [],
        };
        // Ensure promo_codes have an "active" flag for editing
        data.promo_codes = data.promo_codes.map((c) => ({ ...c, active: c.active !== false }));
        setPricing(data);
        setPricingDraft(JSON.parse(JSON.stringify(data)));
      } else if (nextTab === "design") {
        const s = await axios.get(`${API}/bbq-media`);
        setMedia(s.data);
        setMediaDraft({ hero_image: s.data.hero_image || "", detail_image: s.data.detail_image || "" });
        const g = await axios.get(`${API}/bbq-gallery`);
        setGalleryItems(g.data.items || []);
      } else if (nextTab === "revenue") {
        const s = await axios.get(`${API}/bbq-revenue`, { params: { token } });
        setRevenue(s.data);
      } else {
        const r = await axios.get(`${API}/bbq-rsvps`, { params: { token, trashed: nextTab === "trash" } });
        setItems(r.data.items || []);
        const cap = Number(r.data.capacity ?? 50);
        setCapacity(cap);
        setCapacityDraft(String(cap));
      }
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 403 ? "Invalid or missing token" : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "BBQ · Admin Dashboard";
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    axios.get(`${API}/bbq-status`).then((r) => {
      setPageActive(r.data.active !== false);
      setPaymentEmergency(!!r.data.payment_emergency);
    }).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    if (tab === "trash") return null;
    const approved = items.filter((r) => r.approval_status === "approved");
    const pending = items.filter((r) => r.approval_status !== "approved");
    const paid = items.filter((r) => r.paid);
    const unpaid = items.filter((r) => !r.paid);
    const peopleApproved = approved.reduce((s, r) => s + (Number(r.group_size) || 0), 0);
    const peoplePending = pending.reduce((s, r) => s + (Number(r.group_size) || 0), 0);
    const revenuePaid = paid.reduce((s, r) => s + (Number(r.total) || 0), 0);
    const revenueUnpaid = unpaid.reduce((s, r) => s + (Number(r.total) || 0), 0);
    return { peopleApproved, peoplePending, revenuePaid, revenueUnpaid, paidCount: paid.length, unpaidCount: unpaid.length, approvedCount: approved.length, pendingCount: pending.length };
  }, [items, tab]);

  const seatsLeft = Math.max(capacity - (stats?.peopleApproved || 0), 0);
  const fillPct = capacity > 0 && stats ? Math.min(100, (stats.peopleApproved / capacity) * 100) : 0;

  const onSaveCapacity = async () => {
    const n = Number(capacityDraft);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a valid number");
    setSaving(true);
    try {
      await axios.post(`${API}/bbq-settings`, { capacity: Math.floor(n) }, { params: { token } });
      setCapacity(Math.floor(n));
      toast.success(`Capacity updated to ${Math.floor(n)}`);
    } catch {
      toast.error("Failed to update capacity");
    } finally {
      setSaving(false);
    }
  };

  const onSaveEvent = async () => {
    setSavingEvent(true);
    try {
      const r = await axios.post(`${API}/bbq-event`, eventDraft, { params: { token } });
      setEvent(r.data.event);
      setEventDraft(r.data.event);
      toast.success("Event details updated");
    } catch {
      toast.error("Failed to save event");
    } finally {
      setSavingEvent(false);
    }
  };

  const onToggleReviewHidden = async (r) => {
    setReviewBusyId(r.id);
    try {
      await axios.patch(`${API}/bbq-reviews/${r.id}`, { hidden: !r.hidden }, { params: { token } });
      toast.success(!r.hidden ? "Review hidden" : "Review visible");
      await load("reviews");
    } catch {
      toast.error("Failed");
    } finally {
      setReviewBusyId(null);
    }
  };

  const onDeleteReview = async (id) => {
    setReviewBusyId(id);
    try {
      await axios.delete(`${API}/bbq-reviews/${id}`, { params: { token } });
      toast.success("Deleted");
      await load("reviews");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setReviewBusyId(null);
      setConfirm(null);
    }
  };

  const onSaveReviewEdit = async () => {
    if (!reviewEdit) return;
    setReviewBusyId(reviewEdit.id);
    try {
      await axios.patch(
        `${API}/bbq-reviews/${reviewEdit.id}`,
        {
          name: reviewEdit.name,
          rating: reviewEdit.rating,
          text: reviewEdit.text,
          photos: reviewEdit.photos || [],
        },
        { params: { token } }
      );
      toast.success("Review updated");
      setReviewEdit(null);
      await load("reviews");
    } catch {
      toast.error("Failed to save");
    } finally {
      setReviewBusyId(null);
    }
  };

  const onSavePricing = async () => {
    setSavingPricing(true);
    try {
      // Sanitize numbers
      const payload = {
        prices: {
          bbq_only: Number(pricingDraft.prices?.bbq_only) || 0,
          bbq_beer: Number(pricingDraft.prices?.bbq_beer) || 0,
        },
        group_discounts: (pricingDraft.group_discounts || []).map((n) => Number(n) || 0),
        promo_codes: (pricingDraft.promo_codes || [])
          .filter((c) => (c.code || "").trim() !== "")
          .map((c) => ({
            code: String(c.code).trim(),
            amount: Number(c.amount) || 0,
            active: c.active !== false,
          })),
      };
      const r = await axios.post(`${API}/bbq-pricing`, payload, { params: { token } });
      const next = {
        prices: r.data.pricing?.prices || payload.prices,
        group_discounts: r.data.pricing?.group_discounts || payload.group_discounts,
        promo_codes: (r.data.pricing?.promo_codes || payload.promo_codes).map((c) => ({ ...c, active: c.active !== false })),
      };
      setPricing(next);
      setPricingDraft(JSON.parse(JSON.stringify(next)));
      toast.success("Pricing updated");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Invalid token" : "Failed to save pricing");
    } finally {
      setSavingPricing(false);
    }
  };

  const setDraftPrice = (key, value) => {
    setPricingDraft((d) => ({
      ...d,
      prices: { ...(d.prices || {}), [key]: value },
    }));
  };

  const setDraftGroupDiscount = (size, value) => {
    setPricingDraft((d) => {
      const arr = [...(d.group_discounts || [])];
      // ensure length
      while (arr.length <= size) arr.push(0);
      arr[size] = value;
      return { ...d, group_discounts: arr };
    });
  };

  const setDraftPromo = (idx, field, value) => {
    setPricingDraft((d) => {
      const arr = [...(d.promo_codes || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...d, promo_codes: arr };
    });
  };

  const addPromo = () => {
    setPricingDraft((d) => ({
      ...d,
      promo_codes: [...(d.promo_codes || []), { code: "", amount: 5, active: true }],
    }));
  };

  const removePromo = (idx) => {
    setPricingDraft((d) => {
      const arr = [...(d.promo_codes || [])];
      arr.splice(idx, 1);
      return { ...d, promo_codes: arr };
    });
  };

  const setHeroBgMode = async (mode) => {
    try {
      const r = await axios.post(`${API}/bbq-media`, { hero_bg_mode: mode }, { params: { token } });
      setMedia(r.data.media);
      toast.success(mode === "video" ? "히어로 배경: 영상" : "히어로 배경: 이미지");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Invalid token" : "Failed to update");
    }
  };

  const onSaveMedia = async () => {
    setSavingMedia(true);
    try {
      const r = await axios.post(
        `${API}/bbq-media`,
        { hero_image: mediaDraft.hero_image, detail_image: mediaDraft.detail_image },
        { params: { token } }
      );
      setMedia(r.data.media);
      setMediaDraft({ hero_image: r.data.media.hero_image || "", detail_image: r.data.media.detail_image || "" });
      toast.success("Page design saved");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Invalid token" : "Failed to save design");
    } finally {
      setSavingMedia(false);
    }
  };

  const onAddGalleryPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Image files only");
    if (f.size > 12_000_000) return toast.error("Keep image under 12MB");
    setGalleryUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
      await axios.post(`${API}/bbq-gallery`, { image: dataUrl, round: galleryRound.trim() }, { params: { token } });
      const g = await axios.get(`${API}/bbq-gallery`);
      setGalleryItems(g.data.items || []);
      toast.success(galleryRound.trim() ? `"${galleryRound.trim()}" 회차에 추가됨 📸` : "Gallery photo added 📸");
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Invalid token" : "Failed to add photo");
    } finally {
      setGalleryUploading(false);
    }
  };

  const onDeleteGalleryPhoto = async (id) => {
    try {
      await axios.delete(`${API}/bbq-gallery/${id}`, { params: { token } });
      setGalleryItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Failed to remove photo");
    }
  };

  const togglePaymentMode = async () => {
    setTogglingPayMode(true);
    try {
      const next = !paymentEmergency;
      const r = await axios.post(`${API}/bbq-payment-mode`, { emergency: next }, { params: { token } });
      setPaymentEmergency(r.data.emergency);
      toast.success(next ? "비상모드 ON · 계좌이체 안내로 전환" : "비상모드 OFF · 카드 결제로 복귀");
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Invalid token" : "Failed to update payment mode");
    } finally {
      setTogglingPayMode(false);
    }
  };

  const onPickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Video files only (mp4 recommended)");
    if (f.size > 60_000_000) return toast.error("Keep video under 60MB");
    setVideoProgress(0);
    try {
      const start = await axios.post(`${API}/bbq-video/upload/start`, null, { params: { token } });
      const uploadId = start.data.upload_id;
      const CHUNK = 1_000_000;
      const totalChunks = Math.ceil(f.size / CHUNK);
      for (let i = 0; i < totalChunks; i++) {
        const slice = f.slice(i * CHUNK, (i + 1) * CHUNK);
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(slice);
        });
        await axios.post(
          `${API}/bbq-video/upload/chunk`,
          { upload_id: uploadId, index: i, data: b64 },
          { params: { token } }
        );
        setVideoProgress(Math.round(((i + 1) / totalChunks) * 95));
      }
      await axios.post(
        `${API}/bbq-video/upload/finish`,
        { upload_id: uploadId, filename: f.name, content_type: f.type || "video/mp4" },
        { params: { token } }
      );
      toast.success("Promo video uploaded 🎬");
      await load("design");
    } catch {
      toast.error("Video upload failed — please try again");
    } finally {
      setVideoProgress(null);
    }
  };

  const onDeleteVideo = async () => {
    try {
      await axios.delete(`${API}/bbq-video`, { params: { token } });
      toast.success("Video removed");
      await load("design");
    } catch {
      toast.error("Failed to remove video");
    } finally {
      setConfirm(null);
    }
  };

  const onTogglePage = async (nextActive) => {
    setTogglingPage(true);
    try {
      const r = await axios.post(`${API}/bbq-status`, { active: nextActive }, { params: { token } });
      setPageActive(nextActive);
      if (nextActive) {
        toast.success(
          r.data.reset_count > 0
            ? `Page is LIVE · ${r.data.reset_count} RSVPs moved to Trash (fresh start)`
            : "Page is LIVE"
        );
      } else {
        toast.success("Page is now OFFLINE — visitors see a 'coming soon' screen");
      }
      setConfirm(null);
      if (tab === "active" || tab === "trash") await load(tab);
    } catch {
      toast.error("Failed to update page status");
    } finally {
      setTogglingPage(false);
    }
  };

  const onApprove = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/bbq-rsvps/${id}/approve`, null, { params: { token } });
      toast.success("Approved · confirmation email sent");
      await load();
    } catch {
      toast.error("Failed to approve");
    } finally {
      setBusyId(null);
    }
  };

  const onTogglePaid = async (id) => {
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/bbq-rsvps/${id}/paid`, null, { params: { token } });
      toast.success(r.data.paid ? "Marked as PAID" : "Marked as UNPAID");
      await load();
    } catch {
      toast.error("Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  const onTrash = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/bbq-rsvps/${id}/trash`, null, { params: { token } });
      toast.success("Moved to trash");
      await load();
    } catch {
      toast.error("Failed to trash");
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const onRestore = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/bbq-rsvps/${id}/restore`, null, { params: { token } });
      toast.success("Restored");
      await load();
    } catch {
      toast.error("Failed to restore");
    } finally {
      setBusyId(null);
    }
  };

  const onPermaDelete = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/bbq-rsvps/${id}`, { params: { token } });
      toast.success("Permanently deleted");
      await load();
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const downloadCsv = () => {
    const headers = [
      "id", "created_at", "approval_status", "paid", "paid_at", "name", "email", "phone", "social",
      "status", "package", "group_size", "discount_code", "total", "approved_at",
    ];
    const rows = items.map((r) => headers.map((h) => escapeCsv(r[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bbq-rsvps-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#0A0806] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E85A2C]" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[#0A0806] text-white flex flex-col items-center justify-center px-6 text-center">
        <ShieldAlert className="w-10 h-10 text-[#E85A2C] mb-4" />
        <div className="text-xl font-bold mb-2" data-testid="admin-dashboard-error">{error}</div>
        <div className="text-white/50 text-sm">Ensure the URL includes ?token=…</div>
        <Link to="/bbq" className="mt-6 text-[#E85A2C] text-sm underline">Back to BBQ page</Link>
      </main>
    );
  }

  return (
    <main data-testid="bbq-admin-dashboard" className="min-h-screen bg-[#0A0806] text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-5 sm:px-10 py-6 sticky top-0 bg-[#0A0806]/95 backdrop-blur-md z-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <AllRounderLogo variant="mark" color="#FAFAFA" size={32} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">BBQ · Admin</div>
              <div className="text-white text-lg font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} data-testid="admin-refresh-btn" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-white/15 hover:border-[#E85A2C]/50 hover:text-[#E85A2C] transition-colors flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={downloadCsv} data-testid="admin-csv-btn" className="text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-10 py-10 space-y-8">
        {/* Page ON/OFF bar */}
        <div
          data-testid="admin-page-toggle-bar"
          className={`p-4 sm:p-5 rounded-sm border flex flex-wrap items-center justify-between gap-4 ${
            pageActive ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`w-9 h-9 rounded-full flex items-center justify-center ${
                pageActive ? "bg-emerald-500/15" : "bg-red-500/15"
              }`}
            >
              <Power className={`w-4 h-4 ${pageActive ? "text-emerald-400" : "text-red-400"}`} />
            </span>
            <div>
              <div className="text-white font-bold text-sm" style={{ fontFamily: "'Fraunces', serif" }}>
                Public BBQ page is{" "}
                <span className={pageActive ? "text-emerald-400" : "text-red-400"} data-testid="admin-page-status">
                  {pageActive ? "LIVE" : "OFFLINE"}
                </span>
              </div>
              <div className="text-white/50 text-[11px] mt-0.5">
                {pageActive
                  ? "Visitors can view the page and RSVP."
                  : "Visitors see a 'next event coming soon' screen. Turning ON resets all RSVPs (moved to Trash)."}
              </div>
            </div>
          </div>
          <button
            onClick={() => setConfirm({ action: pageActive ? "page-off" : "page-on" })}
            disabled={togglingPage}
            data-testid="admin-page-toggle-btn"
            className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50 ${
              pageActive
                ? "border border-red-500/40 text-red-400 hover:bg-red-500/10"
                : "bg-emerald-600 text-white hover:bg-emerald-500"
            }`}
          >
            {togglingPage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
            {pageActive ? "Turn OFF" : "Turn ON & Reset"}
          </button>
        </div>

        {/* Payment emergency mode */}
        <div
          data-testid="admin-payment-mode-bar"
          className={`mt-3 p-4 sm:p-5 rounded-sm border flex flex-wrap items-center justify-between gap-4 ${
            paymentEmergency ? "border-yellow-500/40 bg-yellow-500/5" : "border-white/10 bg-[#0F0B08]"
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`w-9 h-9 rounded-full flex items-center justify-center ${
                paymentEmergency ? "bg-yellow-500/15" : "bg-white/10"
              }`}
            >
              <CreditCard className={`w-4 h-4 ${paymentEmergency ? "text-yellow-400" : "text-white/60"}`} />
            </span>
            <div>
              <div className="text-white font-bold text-sm" style={{ fontFamily: "'Fraunces', serif" }}>
                Payment mode ·{" "}
                <span className={paymentEmergency ? "text-yellow-400" : "text-emerald-400"} data-testid="admin-payment-mode-status">
                  {paymentEmergency ? "계좌이체 (비상)" : "카드 결제"}
                </span>
              </div>
              <div className="text-white/50 text-[11px] mt-0.5 max-w-md">
                {paymentEmergency
                  ? "카드 결제창 대신 '결제 오류 → 계좌이체' 안내가 표시됩니다."
                  : "정상 카드 결제. 결제 오류 시 ON으로 바꾸면 계좌이체 안내로 전환됩니다."}
              </div>
            </div>
          </div>
          <button
            onClick={togglePaymentMode}
            disabled={togglingPayMode}
            data-testid="admin-payment-mode-toggle"
            className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50 ${
              paymentEmergency
                ? "bg-emerald-600 text-white hover:bg-emerald-500"
                : "border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
            }`}
          >
            {togglingPayMode ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            {paymentEmergency ? "카드 결제로 복귀" : "비상 계좌이체 ON"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10 overflow-x-auto flex-nowrap [&>button]:shrink-0 [-ms-overflow-style:none] [scrollbar-width:none]">
          <button
            onClick={() => setTab("active")}
            data-testid="admin-tab-active"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors ${
              tab === "active" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setTab("trash")}
            data-testid="admin-tab-trash"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "trash" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Trash
          </button>
          <button
            onClick={() => setTab("event")}
            data-testid="admin-tab-event"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "event" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Calendar className="w-3.5 h-3.5" /> Event Details
          </button>
          <button
            onClick={() => setTab("reviews")}
            data-testid="admin-tab-reviews"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "reviews" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Star className="w-3.5 h-3.5" /> Reviews
          </button>
          <button
            onClick={() => setTab("pricing")}
            data-testid="admin-tab-pricing"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "pricing" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Tag className="w-3.5 h-3.5" /> Pricing
          </button>
          <button
            onClick={() => setTab("design")}
            data-testid="admin-tab-design"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "design" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Paintbrush className="w-3.5 h-3.5" /> Design
          </button>
          <button
            onClick={() => setTab("revenue")}
            data-testid="admin-tab-revenue"
            className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold border-b-2 transition-colors flex items-center gap-2 ${
              tab === "revenue" ? "border-[#E85A2C] text-[#E85A2C]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <DollarSign className="w-3.5 h-3.5" /> Revenue
          </button>
        </div>

        {/* Revenue tab */}
        {tab === "revenue" && (
          <section className="space-y-4" data-testid="admin-revenue-section">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-6 rounded-sm border border-[#E85A2C]/25 bg-[#E85A2C]/5" data-testid="revenue-total-card">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Total Revenue</div>
                <div className="text-white text-3xl font-bold mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                  ${Number(revenue.total || 0).toFixed(2)} <span className="text-sm text-white/50">{revenue.currency}</span>
                </div>
              </div>
              <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08]">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/50">Paid Orders</div>
                <div className="text-white text-3xl font-bold mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                  {revenue.count || 0}
                </div>
              </div>
              <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08]">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/50">Guests Paid</div>
                <div className="text-white text-3xl font-bold mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
                  {(revenue.items || []).reduce((s, r) => s + (Number(r.group_size) || 0), 0)}
                </div>
              </div>
            </div>

            {(!revenue.items || revenue.items.length === 0) ? (
              <div className="p-10 text-center text-white/40 border border-white/10 rounded-sm bg-[#0F0B08]" data-testid="revenue-empty">
                No paid transactions yet.
              </div>
            ) : (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="revenue-table">
                    <thead>
                      <tr className="bg-[#0F0B08] text-white/50 text-[10px] uppercase tracking-widest">
                        <th className="text-left px-4 py-3 font-bold">Name</th>
                        <th className="text-left px-4 py-3 font-bold">Package</th>
                        <th className="text-center px-4 py-3 font-bold">Guests</th>
                        <th className="text-right px-4 py-3 font-bold">Amount</th>
                        <th className="text-left px-4 py-3 font-bold">Paid At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenue.items.map((r) => (
                        <tr key={r.session_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`revenue-row-${r.rsvp_id}`}>
                          <td className="px-4 py-3">
                            <div className="text-white font-medium">{r.name}</div>
                            <div className="text-white/40 text-xs">{r.email}</div>
                          </td>
                          <td className="px-4 py-3 text-white/70">{r.package || "—"}</td>
                          <td className="px-4 py-3 text-center text-white/70">{r.group_size}</td>
                          <td className="px-4 py-3 text-right text-[#E85A2C] font-bold">${Number(r.amount).toFixed(2)} {r.currency}</td>
                          <td className="px-4 py-3 text-white/50 text-xs">{fmtDate(r.paid_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Reviews tab */}
        {tab === "reviews" && (
          <section className="space-y-4">
            <div className="p-6 rounded-sm border border-[#E85A2C]/25 bg-[#E85A2C]/5 flex items-center justify-between">
              <div className="flex items-start gap-3">
                <Star className="w-5 h-5 text-[#E85A2C] mt-1 shrink-0 fill-[#E85A2C]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Reviews Moderation</div>
                  <div className="text-white text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {reviews.length} reviews · {reviews.filter((r) => r.hidden).length} hidden
                  </div>
                </div>
              </div>
            </div>
            {reviews.length === 0 ? (
              <div className="p-10 text-center text-white/40 border border-white/10 rounded-sm bg-[#0F0B08]">No reviews yet.</div>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div
                    key={r.id}
                    className={`p-5 rounded-sm border bg-[#0F0B08] ${r.hidden ? "border-white/5 opacity-60" : "border-white/10"}`}
                    data-testid={`admin-review-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-white font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{r.name}</div>
                          {r.hidden && (
                            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border border-white/20 text-white/50">Hidden</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-[#E85A2C] fill-[#E85A2C]" : "text-white/15"}`} strokeWidth={1.5} />
                            ))}
                          </div>
                          <span className="text-white/40 text-[10px] uppercase tracking-widest">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}
                          </span>
                        </div>
                        <p className="mt-3 text-white/75 text-sm leading-relaxed whitespace-pre-wrap">{r.text}</p>
                        {r.photos?.length > 0 && (
                          <div className="mt-3 flex gap-2 flex-wrap">
                            {r.photos.map((p, i) => (
                              <div key={i} className="w-16 h-16 rounded-sm overflow-hidden border border-white/10">
                                <img src={p} alt="" className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => setReviewEdit({ id: r.id, name: r.name, rating: r.rating, text: r.text, photos: r.photos || [] })}
                          data-testid={`admin-review-edit-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-white/15 text-white/70 hover:border-[#E85A2C] hover:text-[#E85A2C] transition-colors flex items-center gap-1.5"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => onToggleReviewHidden(r)}
                          disabled={reviewBusyId === r.id}
                          data-testid={`admin-review-hide-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-white/15 text-white/70 hover:border-yellow-500/50 hover:text-yellow-400 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {r.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {r.hidden ? "Show" : "Hide"}
                        </button>
                        <button
                          onClick={() => setConfirm({ action: "review-delete", id: r.id, name: r.name })}
                          disabled={reviewBusyId === r.id}
                          data-testid={`admin-review-delete-${r.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Event Details editor */}
        {tab === "event" && (
          <section className="space-y-6">
            <div className="p-6 rounded-sm border border-[#E85A2C]/25 bg-[#E85A2C]/5">
              <div className="flex items-start gap-3 mb-1">
                <Calendar className="w-5 h-5 text-[#E85A2C] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Event Details</div>
                  <div className="text-white text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                    Edit what customers see on /bbq
                  </div>
                  <div className="text-white/50 text-[11px] mt-1">Changes appear immediately on the public page after saving.</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <EventField label="Event Title (marquee)" helper='Big scrolling text at top. e.g. "South Bank BBQ Party"'
                value={eventDraft.title} onChange={(v) => setEventDraft({ ...eventDraft, title: v })} testid="event-title" />
              <EventField label="Eyebrow tagline" helper='Small orange tag above title. e.g. "South Bank Social · Aug 7"'
                value={eventDraft.eyebrow} onChange={(v) => setEventDraft({ ...eventDraft, eyebrow: v })} testid="event-eyebrow" />

              <EventField label="Date (main)" helper='e.g. "Fri, Aug 7"' icon={Calendar}
                value={eventDraft.date_label} onChange={(v) => setEventDraft({ ...eventDraft, date_label: v })} testid="event-date" />
              <EventField label="Date note (subtitle)" helper='e.g. "Rain or shine — covered pavilion secured"'
                value={eventDraft.date_note} onChange={(v) => setEventDraft({ ...eventDraft, date_note: v })} testid="event-date-note" />

              <EventField label="Time (main)" helper='e.g. "5:00 – 8:00 PM"' icon={Clock}
                value={eventDraft.time_label} onChange={(v) => setEventDraft({ ...eventDraft, time_label: v })} testid="event-time" />
              <EventField label="Time note (subtitle)" helper='e.g. "Arrive from 4:45 to help fire the grill"'
                value={eventDraft.time_note} onChange={(v) => setEventDraft({ ...eventDraft, time_note: v })} testid="event-time-note" />

              <EventField label="Location (main)" helper='e.g. "South Bank Park"' icon={MapPin}
                value={eventDraft.location_label} onChange={(v) => setEventDraft({ ...eventDraft, location_label: v })} testid="event-location" />
              <EventField label="Location note (subtitle)" helper='e.g. "Riverside pavilion · closest to the Wheel"'
                value={eventDraft.location_note} onChange={(v) => setEventDraft({ ...eventDraft, location_note: v })} testid="event-location-note" />

              <EventField label="Location — Big area heading" helper='Big text on left. e.g. "South Bank"'
                value={eventDraft.location_area} onChange={(v) => setEventDraft({ ...eventDraft, location_area: v })} testid="event-location-area" />
              <EventField label="Location — Region" helper='e.g. "QLD · Australia"'
                value={eventDraft.location_region} onChange={(v) => setEventDraft({ ...eventDraft, location_region: v })} testid="event-location-region" />

              <EventField label="Short Date (success screen)" helper='Compact form. e.g. "Fri, Aug 7"'
                value={eventDraft.short_date} onChange={(v) => setEventDraft({ ...eventDraft, short_date: v })} testid="event-short-date" />
              <EventField label="Short Time (success screen)" helper='Compact form. e.g. "5–8 PM"'
                value={eventDraft.short_time} onChange={(v) => setEventDraft({ ...eventDraft, short_time: v })} testid="event-short-time" />
              <EventField label="Short Location (success screen)" helper='Compact form. e.g. "South Bank"'
                value={eventDraft.short_location} onChange={(v) => setEventDraft({ ...eventDraft, short_location: v })} testid="event-short-location" />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => setEventDraft(event)}
                data-testid="admin-event-reset"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset changes
              </button>
              <button
                onClick={onSaveEvent}
                disabled={savingEvent}
                data-testid="admin-event-save"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {savingEvent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save all changes
              </button>
            </div>
          </section>
        )}

        {/* Design editor */}
        {tab === "design" && (
          <section className="space-y-6" data-testid="admin-design-section">
            <div className="p-6 rounded-sm border border-[#E85A2C]/25 bg-[#E85A2C]/5">
              <div className="flex items-start gap-3 mb-1">
                <Paintbrush className="w-5 h-5 text-[#E85A2C] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Page Design · 꾸미기</div>
                  <div className="text-white text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                    Photos & promo video on /bbq
                  </div>
                  <div className="text-white/50 text-[11px] mt-1">
                    Upload your own photos and promo video. Changes appear immediately on the public page.
                  </div>
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ImagePicker
                label="Hero Background"
                helper="Big photo at the top of /bbq — also shows semi-transparent behind the RSVP text. Landscape works best · max 10MB"
                value={mediaDraft.hero_image}
                fallback={HERO_FALLBACK}
                onChange={(v) => setMediaDraft((d) => ({ ...d, hero_image: v }))}
                testid="design-hero"
              />
              <ImagePicker
                label="Details Section Photo"
                helper="Portrait photo next to date/time/location · max 10MB"
                value={mediaDraft.detail_image}
                fallback={DETAIL_FALLBACK}
                onChange={(v) => setMediaDraft((d) => ({ ...d, detail_image: v }))}
                testid="design-detail"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => setMediaDraft({ hero_image: media.hero_image || "", detail_image: media.detail_image || "" })}
                data-testid="admin-design-reset"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset changes
              </button>
              <button
                onClick={onSaveMedia}
                disabled={savingMedia}
                data-testid="admin-design-save"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {savingMedia ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save photos
              </button>
            </div>

            {/* Gallery — auto-slide carousel on /bbq (grouped by round) */}
            <div className="p-5 sm:p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-4" data-testid="admin-gallery-block">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C] flex items-center gap-2">
                    <Camera className="w-3.5 h-3.5" /> Gallery · 갤러리 (회차별)
                  </div>
                  <div className="text-white/60 text-[11px] mt-1 max-w-md">
                    회차 이름을 입력하고 사진을 올리면 그 회차에 담깁니다. /bbq 하단에서 회차 탭으로 나뉘어 표시됩니다. 사진당 최대 12MB.
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={galleryRound}
                  onChange={(e) => setGalleryRound(e.target.value)}
                  placeholder="회차 이름 (예: 1회차 · 2026 여름)"
                  data-testid="admin-gallery-round-input"
                  className="flex-1 min-w-[180px] h-10 rounded-sm bg-[#0A0806] border border-white/15 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E85A2C]"
                />
                <label
                  data-testid="admin-gallery-upload"
                  className={`cursor-pointer inline-flex text-[10px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors items-center gap-2 ${galleryUploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  {galleryUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {galleryRound.trim() ? `"${galleryRound.trim()}"에 추가` : "사진 추가"}
                  <input type="file" accept="image/*" className="hidden" onChange={onAddGalleryPhoto} />
                </label>
              </div>

              {galleryItems.length === 0 ? (
                <div className="p-6 text-center text-white/40 border border-dashed border-white/10 rounded-sm text-xs uppercase tracking-widest">
                  No gallery photos yet.
                </div>
              ) : (
                <div className="space-y-5">
                  {Object.entries(
                    galleryItems.reduce((acc, g) => {
                      const key = (g.round || "").trim() || "기본";
                      (acc[key] = acc[key] || []).push(g);
                      return acc;
                    }, {})
                  ).map(([roundName, items]) => (
                    <div key={roundName} data-testid={`admin-gallery-round-${roundName}`}>
                      <div className="text-[11px] uppercase tracking-widest font-bold text-white/70 mb-2 flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full bg-[#E85A2C]/15 text-[#E85A2C]">{roundName}</span>
                        <span className="text-white/30">{items.length}장</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {items.map((g) => (
                          <div key={g.id} className="relative group rounded-sm overflow-hidden border border-white/10 bg-black/40 aspect-square" data-testid={`admin-gallery-item-${g.id}`}>
                            <img src={g.image} alt="" className="w-full h-full object-cover" />
                            <button
                              onClick={() => onDeleteGalleryPhoto(g.id)}
                              data-testid={`admin-gallery-delete-${g.id}`}
                              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-red-400 hover:bg-red-500/80 hover:text-white flex items-center justify-center transition-colors"
                              aria-label="Delete photo"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hero background mode: image or video */}
            <div className="p-5 sm:p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-3" data-testid="admin-hero-bg-block">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C] flex items-center gap-2">
                <Film className="w-3.5 h-3.5" /> 히어로 배경 (맨 위 로고 뒤 큰 배경)
              </div>
              <div className="text-white/60 text-[11px]">
                이미지 = 위 Hero 사진 / 영상 = 아래 Promo 영상을 배경으로 재생(음소거·반복). 영상 선택 시 Promo 영상을 먼저 업로드하세요.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setHeroBgMode("image")}
                  data-testid="admin-hero-bg-image"
                  className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm transition-colors ${media.hero_bg_mode !== "video" ? "bg-[#0A369D] text-white" : "border border-white/15 text-white/60 hover:text-white"}`}
                >
                  이미지
                </button>
                <button
                  onClick={() => setHeroBgMode("video")}
                  data-testid="admin-hero-bg-video"
                  className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm transition-colors ${media.hero_bg_mode === "video" ? "bg-[#E85A2C] text-white" : "border border-white/15 text-white/60 hover:text-white"}`}
                >
                  영상
                </button>
              </div>
            </div>

            {/* Promo video */}
            <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C] flex items-center gap-2">
                    <Film className="w-3.5 h-3.5" /> Promo Video
                  </div>
                  <div className="text-white/60 text-[11px] mt-1">
                    Shows as a "Promo Film" section on /bbq. MP4 recommended · max 60MB.
                  </div>
                </div>
                {media.video_id && (
                  <button
                    onClick={() => setConfirm({ action: "video-delete" })}
                    data-testid="admin-video-delete"
                    className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" /> Remove video
                  </button>
                )}
              </div>

              {media.video_id ? (
                <video
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="admin-video-preview"
                  className="w-full max-w-xl aspect-video bg-black rounded-sm border border-white/10"
                  src={`${API}/bbq-video?v=${encodeURIComponent(media.video_updated_at || "")}`}
                />
              ) : (
                <div className="p-6 text-center text-white/40 border border-dashed border-white/10 rounded-sm text-xs uppercase tracking-widest">
                  No promo video uploaded yet.
                </div>
              )}

              {videoProgress !== null ? (
                <div data-testid="admin-video-progress">
                  <div className="flex items-center justify-between text-[11px] text-white/60 mb-1.5">
                    <span className="uppercase tracking-widest font-bold">Uploading…</span>
                    <span className="tabular">{videoProgress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-[#E85A2C] transition-all" style={{ width: `${videoProgress}%` }} />
                  </div>
                </div>
              ) : (
                <label
                  data-testid="admin-video-upload"
                  className="cursor-pointer inline-flex text-[10px] uppercase tracking-widest font-bold px-4 py-2.5 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors items-center gap-2"
                >
                  <Upload className="w-3.5 h-3.5" /> {media.video_id ? "Replace video" : "Upload video"}
                  <input type="file" accept="video/*" className="hidden" onChange={onPickVideo} />
                </label>
              )}
            </div>
          </section>
        )}

        {/* Pricing editor */}
        {tab === "pricing" && (
          <section className="space-y-6" data-testid="admin-pricing-section">
            <div className="p-6 rounded-sm border border-[#E85A2C]/25 bg-[#E85A2C]/5">
              <div className="flex items-start gap-3 mb-1">
                <Tag className="w-5 h-5 text-[#E85A2C] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Pricing & Promotions</div>
                  <div className="text-white text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>
                    Control BBQ ticket costs
                  </div>
                  <div className="text-white/50 text-[11px] mt-1">
                    Edit package prices, group discounts and promo codes. Changes take effect immediately on the public RSVP form.
                  </div>
                </div>
              </div>
            </div>

            {/* Package prices */}
            <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Ticket Price</div>
                <div className="text-white/60 text-[11px] mt-1">One price for everyone · per-person price in AUD for each package.</div>
              </div>

              <div className="p-4 rounded-sm border border-white/10 bg-black/30 space-y-3 max-w-xl">
                <div className="text-white font-bold text-sm" style={{ fontFamily: "'Fraunces', serif" }}>General (per person)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">BBQ Only</label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                      <input
                        type="number" min="0" step="1"
                        value={pricingDraft.prices?.bbq_only ?? ""}
                        onChange={(e) => setDraftPrice("bbq_only", e.target.value)}
                        data-testid="price-bbq-only"
                        className="w-full bg-black/40 border border-white/15 rounded-sm pl-8 pr-3 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">BBQ + Beer</label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                      <input
                        type="number" min="0" step="1"
                        value={pricingDraft.prices?.bbq_beer ?? ""}
                        onChange={(e) => setDraftPrice("bbq_beer", e.target.value)}
                        data-testid="price-bbq-beer"
                        className="w-full bg-black/40 border border-white/15 rounded-sm pl-8 pr-3 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Group discounts */}
            <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Group Discounts</div>
                <div className="text-white/60 text-[11px] mt-1">Per-person discount (AUD) applied for each group size (1–9 people).</div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((size) => (
                  <div key={size}>
                    <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">
                      {size} pax
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                      <input
                        type="number" min="0" step="1"
                        value={pricingDraft.group_discounts?.[size] ?? 0}
                        onChange={(e) => setDraftGroupDiscount(size, e.target.value)}
                        data-testid={`group-discount-${size}`}
                        className="w-full bg-black/40 border border-white/15 rounded-sm pl-8 pr-2 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Promo codes */}
            <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08] space-y-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]">Promo Codes</div>
                  <div className="text-white/60 text-[11px] mt-1">Per-person discount (AUD) applied when a valid code is entered.</div>
                </div>
                <button
                  onClick={addPromo}
                  data-testid="promo-add"
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm border border-[#E85A2C]/40 text-[#E85A2C] hover:bg-[#E85A2C]/10 transition-colors flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Add code
                </button>
              </div>

              {(pricingDraft.promo_codes || []).length === 0 ? (
                <div className="p-6 text-center text-white/40 border border-dashed border-white/10 rounded-sm text-xs uppercase tracking-widest">
                  No promo codes. Click “Add code”.
                </div>
              ) : (
                <div className="space-y-2">
                  {(pricingDraft.promo_codes || []).map((c, idx) => (
                    <div
                      key={idx}
                      data-testid={`promo-row-${idx}`}
                      className="grid grid-cols-12 gap-2 items-end p-3 rounded-sm border border-white/10 bg-black/30"
                    >
                      <div className="col-span-12 sm:col-span-5">
                        <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">Code</label>
                        <input
                          type="text"
                          value={c.code || ""}
                          onChange={(e) => setDraftPromo(idx, "code", e.target.value)}
                          data-testid={`promo-code-${idx}`}
                          placeholder="e.g. ard0811"
                          className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C]"
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">Discount (per pax)</label>
                        <div className="relative">
                          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                          <input
                            type="number" min="0" step="1"
                            value={c.amount ?? 0}
                            onChange={(e) => setDraftPromo(idx, "amount", e.target.value)}
                            data-testid={`promo-amount-${idx}`}
                            className="w-full bg-black/40 border border-white/15 rounded-sm pl-8 pr-2 py-2.5 text-white text-sm outline-none focus:border-[#E85A2C]"
                          />
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/70 mb-1.5 block">Active</label>
                        <button
                          onClick={() => setDraftPromo(idx, "active", !(c.active !== false))}
                          data-testid={`promo-active-${idx}`}
                          className={`w-full px-3 py-2.5 rounded-sm text-[11px] uppercase tracking-widest font-bold border transition-colors ${
                            c.active !== false
                              ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                              : "border-white/15 text-white/40"
                          }`}
                        >
                          {c.active !== false ? "On" : "Off"}
                        </button>
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => removePromo(idx)}
                          data-testid={`promo-remove-${idx}`}
                          className="w-full sm:w-auto px-3 py-2.5 rounded-sm text-white/50 hover:text-red-400 border border-white/10 hover:border-red-500/40 transition-colors flex items-center justify-center"
                          title="Remove code"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => setPricingDraft(JSON.parse(JSON.stringify(pricing)))}
                data-testid="admin-pricing-reset"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset changes
              </button>
              <button
                onClick={onSavePricing}
                disabled={savingPricing}
                data-testid="admin-pricing-save"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {savingPricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save pricing
              </button>
            </div>
          </section>
        )}

        {tab === "active" && stats && (
          <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-6 rounded-sm border border-[#E85A2C]/30 bg-[#E85A2C]/5">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#E85A2C] mb-2">Capacity</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={capacityDraft}
                  onChange={(e) => setCapacityDraft(e.target.value)}
                  data-testid="admin-capacity-input"
                  className="w-20 bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-white text-xl font-bold outline-none focus:border-[#E85A2C]"
                  style={{ fontFamily: "'Fraunces', serif" }}
                />
                <span className="text-white/50 text-sm">people</span>
                <button
                  onClick={onSaveCapacity}
                  disabled={saving || Number(capacityDraft) === capacity}
                  data-testid="admin-capacity-save"
                  className="ml-auto text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded-sm bg-white text-black hover:bg-white/90 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              </div>
              <div className="mt-4">
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#E85A2C] transition-all" style={{ width: `${fillPct}%` }} />
                </div>
                <div className="mt-2 text-[11px] text-white/60">
                  <span className="text-white font-bold">{stats.peopleApproved}</span> approved · <span className="text-white font-bold">{seatsLeft}</span> seats left
                </div>
              </div>
            </div>

            <div className="p-6 rounded-sm border border-white/10 bg-[#0F0B08]">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2">Approved</div>
              <div className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }} data-testid="admin-stat-approved">
                {stats.peopleApproved}<span className="text-white/40 text-sm font-normal"> / {capacity}</span>
              </div>
              <div className="mt-1 text-[11px] text-white/50">{stats.approvedCount} bookings · Pending {stats.pendingCount}</div>
            </div>

            <div className="p-6 rounded-sm border border-emerald-500/20 bg-emerald-500/5">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-400 mb-2">Paid</div>
              <div className="text-3xl font-bold text-emerald-400" style={{ fontFamily: "'Fraunces', serif" }} data-testid="admin-stat-paid">
                ${stats.revenuePaid.toFixed(0)}
              </div>
              <div className="mt-1 text-[11px] text-white/60">{stats.paidCount} bookings</div>
            </div>

            <div className="p-6 rounded-sm border border-yellow-500/20 bg-yellow-500/5">
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-yellow-400 mb-2">Unpaid</div>
              <div className="text-3xl font-bold text-yellow-400" style={{ fontFamily: "'Fraunces', serif" }} data-testid="admin-stat-unpaid">
                ${stats.revenueUnpaid.toFixed(0)}
              </div>
              <div className="mt-1 text-[11px] text-white/60">{stats.unpaidCount} bookings</div>
            </div>
          </section>
        )}

        {/* Table */}
        {(tab === "active" || tab === "trash") && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#E85A2C]" />
              <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-white/70">
                {tab === "trash" ? "Trash · " : "All RSVPs · "}{items.length}
              </div>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="p-10 text-center text-white/40 border border-white/10 rounded-sm bg-[#0F0B08]">
              {tab === "trash" ? "Trash is empty." : "No RSVPs yet."}
            </div>
          ) : (
            <div className="overflow-x-auto border border-white/10 rounded-sm bg-[#0F0B08]">
              <table className="w-full min-w-[1100px] text-sm" data-testid="admin-rsvp-table">
                <thead>
                  <tr className="border-b border-white/10 text-white/50 text-[10px] uppercase tracking-widest">
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="text-left px-4 py-3 font-bold">Payment</th>
                    <th className="text-left px-4 py-3 font-bold">Name</th>
                    <th className="text-left px-4 py-3 font-bold">Contact</th>
                    <th className="text-left px-4 py-3 font-bold">Package</th>
                    <th className="text-right px-4 py-3 font-bold">Pax</th>
                    <th className="text-right px-4 py-3 font-bold">Total</th>
                    <th className="text-left px-4 py-3 font-bold">Code</th>
                    <th className="text-left px-4 py-3 font-bold">Created</th>
                    <th className="text-right px-4 py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">{statusPill(r)}</td>
                      <td className="px-4 py-3">{paidPill(r)}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white">{r.name}</div>
                        {r.social && <div className="text-white/40 text-[11px]">{r.social}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <a href={`mailto:${r.email}`} className="text-white/80 text-[13px] hover:text-[#E85A2C]">{r.email}</a>
                        <div className="text-white/40 text-[11px]">{r.phone || "-"}</div>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-[12px]">
                        <div>{r.status}</div>
                        <div className="text-white/40 text-[11px]">{r.package}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-semibold">{r.group_size}</td>
                      <td className="px-4 py-3 text-right text-white font-bold">${Number(r.total).toFixed(0)}</td>
                      <td className="px-4 py-3 text-[11px] text-[#E85A2C]">{r.discount_code || "-"}</td>
                      <td className="px-4 py-3 text-white/50 text-[11px] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        {tab === "active" ? (
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            <button
                              onClick={() => onTogglePaid(r.id)}
                              disabled={busyId === r.id}
                              data-testid={`admin-paid-toggle-${r.id}`}
                              title={r.paid ? "Mark as UNPAID" : "Mark as PAID"}
                              className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm border transition-colors flex items-center gap-1.5 disabled:opacity-50 ${
                                r.paid
                                  ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                                  : "border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
                              }`}
                            >
                              <DollarSign className="w-3 h-3" /> {r.paid ? "Paid" : "Mark paid"}
                            </button>
                            {r.approval_status !== "approved" && (
                              <button
                                onClick={() => onApprove(r.id)}
                                disabled={busyId === r.id}
                                data-testid={`admin-approve-${r.id}`}
                                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                              >
                                <Check className="w-3 h-3" /> Approve
                              </button>
                            )}
                            <button
                              onClick={() => setConfirm({ action: "trash", id: r.id, name: r.name })}
                              disabled={busyId === r.id}
                              data-testid={`admin-trash-${r.id}`}
                              title="Move to trash"
                              className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm border border-white/20 text-white/60 hover:border-red-500/50 hover:text-red-400 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            <button
                              onClick={() => onRestore(r.id)}
                              disabled={busyId === r.id}
                              data-testid={`admin-restore-${r.id}`}
                              className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <Undo2 className="w-3 h-3" /> Restore
                            </button>
                            <button
                              onClick={() => setConfirm({ action: "delete", id: r.id, name: r.name })}
                              disabled={busyId === r.id}
                              data-testid={`admin-perma-${r.id}`}
                              className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm bg-red-600 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <X className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        <div className="pt-6 text-center">
          <Link to="/bbq" className="text-white/40 hover:text-[#E85A2C] text-[11px] uppercase tracking-widest transition-colors">
            ← Back to public BBQ page
          </Link>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6" data-testid="admin-confirm-modal">
          <div className="max-w-sm w-full bg-[#0F0B08] border border-white/15 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              {confirm.action === "page-on" ? (
                <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <Power className="w-5 h-5 text-emerald-400" />
                </div>
              ) : confirm.action === "page-off" ? (
                <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                  <Power className="w-5 h-5 text-red-400" />
                </div>
              ) : (confirm.action === "delete" || confirm.action === "review-delete" || confirm.action === "video-delete") ? (
                <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                  <X className="w-5 h-5 text-red-400" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-yellow-500/15 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-yellow-400" />
                </div>
              )}
              <div className="text-white font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>
                {confirm.action === "page-on"
                  ? "Turn page ON & reset?"
                  : confirm.action === "page-off"
                  ? "Turn page OFF?"
                  : confirm.action === "video-delete"
                  ? "Remove promo video?"
                  : confirm.action === "review-delete"
                  ? "Delete review?"
                  : confirm.action === "delete"
                  ? "Permanently delete?"
                  : "Move to trash?"}
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {confirm.action === "page-on" ? (
                <>The BBQ page will go <b className="text-emerald-400">LIVE</b> and{" "}
                <b className="text-white">ALL current RSVPs will be moved to Trash</b> — the attendee count restarts from 0 for the new event.</>
              ) : confirm.action === "page-off" ? (
                <>Visitors will see a <b className="text-white">&ldquo;next event coming soon&rdquo;</b> screen and new RSVPs will be blocked. Existing data is kept.</>
              ) : confirm.action === "video-delete" ? (
                <>The promo video will be removed from the public page. You can upload a new one anytime.</>
              ) : confirm.action === "review-delete" ? (
                <>Permanently delete <b className="text-white">{confirm.name}</b>&apos;s review. This cannot be undone.</>
              ) : confirm.action === "delete" ? (
                <>This will permanently remove <b className="text-white">{confirm.name}</b>&apos;s RSVP. It cannot be undone.</>
              ) : (
                <>Move <b className="text-white">{confirm.name}</b>&apos;s RSVP to trash. They will no longer count toward capacity. You can restore anytime.</>
              )}
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setConfirm(null)}
                data-testid="admin-confirm-cancel"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirm.action === "page-on") return onTogglePage(true);
                  if (confirm.action === "page-off") return onTogglePage(false);
                  if (confirm.action === "video-delete") return onDeleteVideo();
                  if (confirm.action === "review-delete") return onDeleteReview(confirm.id);
                  if (confirm.action === "delete") return onPermaDelete(confirm.id);
                  return onTrash(confirm.id);
                }}
                disabled={busyId === confirm.id || reviewBusyId === confirm.id || togglingPage}
                data-testid="admin-confirm-confirm"
                className={`text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm text-white transition-colors flex items-center gap-2 disabled:opacity-50 ${
                  confirm.action === "page-on"
                    ? "bg-emerald-600 hover:bg-emerald-500"
                    : (confirm.action === "delete" || confirm.action === "review-delete" || confirm.action === "video-delete" || confirm.action === "page-off")
                    ? "bg-red-600 hover:bg-red-500"
                    : "bg-yellow-600 hover:bg-yellow-500"
                }`}
              >
                {(busyId === confirm.id || reviewBusyId === confirm.id || togglingPage)
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : (confirm.action === "page-on" || confirm.action === "page-off")
                    ? <Power className="w-3.5 h-3.5" />
                    : (confirm.action === "delete" || confirm.action === "review-delete" || confirm.action === "video-delete")
                    ? <X className="w-3.5 h-3.5" />
                    : <Trash2 className="w-3.5 h-3.5" />}
                {confirm.action === "page-on"
                  ? "Go LIVE & reset"
                  : confirm.action === "page-off"
                  ? "Turn OFF"
                  : confirm.action === "video-delete"
                  ? "Remove video"
                  : confirm.action === "review-delete"
                  ? "Delete review"
                  : confirm.action === "delete"
                  ? "Delete forever"
                  : "Move to trash"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Edit Modal */}
      {reviewEdit && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-6" data-testid="admin-review-edit-modal">
          <div className="max-w-lg w-full bg-[#0F0B08] border border-white/15 rounded-sm p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <Pencil className="w-4 h-4 text-[#E85A2C]" />
              <div className="text-white font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>Edit review</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-1.5 block">Name</label>
              <input
                type="text"
                value={reviewEdit.name}
                onChange={(e) => setReviewEdit({ ...reviewEdit, name: e.target.value })}
                data-testid="admin-review-edit-name"
                className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-[#E85A2C]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-1.5 block">Rating</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setReviewEdit({ ...reviewEdit, rating: n })}
                    data-testid={`admin-review-edit-star-${n}`}
                  >
                    <Star className={`w-6 h-6 ${n <= reviewEdit.rating ? "text-[#E85A2C] fill-[#E85A2C]" : "text-white/20"}`} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-1.5 block">Message</label>
              <textarea
                rows={5}
                value={reviewEdit.text}
                onChange={(e) => setReviewEdit({ ...reviewEdit, text: e.target.value })}
                data-testid="admin-review-edit-text"
                className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-white outline-none focus:border-[#E85A2C] resize-none"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">Photos</label>
                <span className="text-[10px] text-white/40">{(reviewEdit.photos || []).length}/6</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {(reviewEdit.photos || []).map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-white/15 group" data-testid={`admin-review-edit-photo-${i}`}>
                    <img src={p} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setReviewEdit({ ...reviewEdit, photos: reviewEdit.photos.filter((_, j) => j !== i) })}
                      data-testid={`admin-review-edit-photo-remove-${i}`}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="remove"
                    >
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
                {(reviewEdit.photos || []).length < 6 && (
                  <label
                    className="aspect-square rounded-sm border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#E85A2C]/50 transition-colors bg-black/20"
                    data-testid="admin-review-edit-photo-add"
                  >
                    <Camera className="w-5 h-5 text-white/50" />
                    <span className="text-[9px] uppercase tracking-widest text-white/50">Add</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []).slice(0, 6 - (reviewEdit.photos || []).length);
                        files.forEach((f) => {
                          if (!f.type.startsWith("image/")) return;
                          if (f.size > 2_000_000) {
                            toast.error(`${f.name} is over 2MB`);
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () =>
                            setReviewEdit((prev) => {
                              if (!prev) return prev;
                              const existing = prev.photos || [];
                              if (existing.length >= 6) return prev;
                              return { ...prev, photos: [...existing, reader.result] };
                            });
                          reader.readAsDataURL(f);
                        });
                        e.target.value = "";
                      }}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end pt-2 border-t border-white/5">
              <button
                onClick={() => setReviewEdit(null)}
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm border border-white/15 text-white/60 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSaveReviewEdit}
                disabled={reviewBusyId === reviewEdit.id}
                data-testid="admin-review-edit-save"
                className="text-[11px] uppercase tracking-widest font-bold px-4 py-2 rounded-sm bg-[#E85A2C] text-white hover:bg-[#f26a3c] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {reviewBusyId === reviewEdit.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
